/**
 * The one place a server response is checked against what we believe it is.
 *
 * It throws on a mismatch, and the screen says it cannot read the answer.
 * That is the point: silent drift between a frontend type and a backend
 * response is the failure this layer exists to prevent, and a screen that
 * half-renders is worse than one that stops.
 *
 * The cost is real — a backend that drops a field breaks the screen on deploy
 * skew rather than degrading. Every call goes through here so that policy is
 * one function to soften if it proves wrong.
 */
import { z } from 'zod';

export class ResponseShapeError extends Error {
	constructor(
		readonly path: string,
		readonly issues: z.ZodIssue[],
	) {
		super(
			`The API's answer for ${path} was not the shape this app expects.`,
		);
		this.name = 'ResponseShapeError';
	}
}

/**
 * Generic over the SCHEMA, not over its output type.
 *
 * `z.ZodType<T>` expands to `ZodType<T, ZodTypeDef, T>` — its input parameter
 * defaults to its output. That holds for most schemas and breaks on `.catch()`,
 * which accepts `unknown` and falls back, so its input and output differ. Asked
 * to satisfy both at once, the compiler widened those fields to `unknown`:
 * `analysisRunDetailSchema.note` is `z.string().catch('')`, and the run it
 * parsed no longer matched the `AnalysisRunDetail` the table takes, though both
 * names pointed at that one schema.
 *
 * Inferring `S` and returning `z.infer<S>` reads the output side only, which is
 * what a parsed response is.
 */
export function parsed<S extends z.ZodTypeAny>(
	schema: S,
	data: unknown,
	path: string,
): z.infer<S> {
	const result = schema.safeParse(data);
	if (result.success) return result.data;

	/* Logged in full because the thrown message is written for a person on a
     screen, and the issue list is written for whoever fixes the drift. */
	console.error(`[schema] ${path} did not match:`, result.error.issues, data);
	throw new ResponseShapeError(path, result.error.issues);
}
